import { MenuTipDollarSettingsBuilder } from "./dollar-settings/menu.dollar.builder";
import { MenuTipPercentageSettingsBuilder } from "./percentage-settings/menu.percentage.builder";
import { DocumentData } from "@angular/fire/firestore";
import { MenuPaymentSettings } from "./menu.payment.model";
import { MenuDistributionSettingsBuilder } from "./distribution-settings/menu.distribution.builder";
import { MenuDistributionType } from "./distribution-settings/menu.distribution.enum";

export class MenuPaymentSettingsBuilder {
  private _isFeeActive: boolean;
  private _isTippingActive: boolean;
  private _isDistributeTippingActive: boolean;
  private _fee: number;
  private _isFeePercentage: boolean;
  private _dollar: MenuTipDollarSettingsBuilder;
  private _fiveToTen: MenuTipDollarSettingsBuilder;
  private _percentage: MenuTipPercentageSettingsBuilder;
  private _distribution: MenuDistributionSettingsBuilder;
  constructor() {
    this.default();
  }

  public get fee(): number {
    return this._fee;
  }
  public set fee(n: number) {
    this._fee = n;
  }

  public get isFeeActive(): boolean {
    return this._isFeeActive;
  }

  public set isFeeActive(b: boolean) {
    this._isFeeActive = b;
  }
  public get isFeePercentage(): boolean {
    return this._isFeePercentage;
  }
  public set isFeePercentage(value: boolean) {
    this._isFeePercentage = value;
  }

  public get isTippingActive(): boolean {
    return this._isTippingActive;
  }

  public set isTippingActive(b: boolean) {
    this._isTippingActive = b;
  }

  public get isDistributeTippingActive(): boolean {
    return this._isDistributeTippingActive;
  }

  public set isDistributeTippingActive(b: boolean) {
    this._isDistributeTippingActive = b;
  }
  public get distributionType(): MenuDistributionType {
    return this._distribution.type;
  }

  public set distributionType(type: MenuDistributionType) {
    this._distribution.type = type;
  }
  public get distributionLabels(): string[] {
    return this._distribution.labels;
  }
  public set distributionLabels(labels: string[]) {
    this._distribution.labels = labels;
  }

  public get dollar(): MenuTipDollarSettingsBuilder {
    return this._dollar;
  }
  public get fiveToTen(): MenuTipDollarSettingsBuilder {
    return this._fiveToTen;
  }
  public get percentage(): MenuTipPercentageSettingsBuilder {
    return this._percentage;
  }
  public get distribution(): MenuDistributionSettingsBuilder {
    return this._distribution;
  }

  public get percentMinimum(): number {
    return this._percentage.minimum;
  }

  public set percentMinimum(n: number) {
    this._percentage.minimum = n;
  }

  public get percentOne(): number {
    return this._percentage.one;
  }

  public set percentOne(n: number) {
    this._percentage.one = n;
  }

  public get percentTwo(): number {
    return this._percentage.two;
  }

  public set percentTwo(n: number) {
    this._percentage.two = n;
  }

  public get percentThree(): number {
    return this._percentage.three;
  }

  public set percentThree(n: number) {
    this._percentage.three = n;
  }

  public get dollarOne(): number {
    return this._dollar.one;
  }

  public set dollarOne(n: number) {
    this._dollar.one = n;
  }

  public get dollarTwo(): number {
    return this._dollar.two;
  }

  public set dollarTwo(n: number) {
    this._dollar.two = n;
  }

  public get dollarThree(): number {
    return this._dollar.three;
  }

  public set dollarThree(n: number) {
    this._dollar.three = n;
  }

  public get dollarMinimum(): number {
    return this._dollar.minimum;
  }

  public set dollarMinimum(n: number) {
    this._dollar.minimum = n;
  }

  public get fiveToTenMinimum(): number {
    return this._fiveToTen.minimum;
  }
  public get fiveToTenOne(): number {
    return this._fiveToTen.one;
  }
  public get fiveToTenTwo(): number {
    return this._fiveToTen.two;
  }
  public get fiveToTenThree(): number {
    return this._fiveToTen.three;
  }

  public set fiveToTenMinimum(n: number) {
    this._fiveToTen.minimum = n;
  }
  public set fiveToTenOne(n: number) {
    this._fiveToTen.one = n;
  }
  public set fiveToTenTwo(n: number) {
    this._fiveToTen.two = n;
  }
  public set fiveToTenThree(n: number) {
    this._fiveToTen.three = n;
  }

  private default(): void {
    this.isFeeActive = false;
    this._isFeePercentage = false;
    this.fee = 1;
    this.isDistributeTippingActive = false;
    this.isTippingActive = true;
    this._dollar = new MenuTipDollarSettingsBuilder();
    this._fiveToTen = new MenuTipDollarSettingsBuilder().forFiveToTen();
    this._percentage = new MenuTipPercentageSettingsBuilder();
    this._distribution = new MenuDistributionSettingsBuilder();
  }

  public load(data: DocumentData): MenuPaymentSettingsBuilder {
    this._isFeeActive = data.isFeeActive;
    this._isFeePercentage = data.isFeePercentage ?? false;
    this._isDistributeTippingActive = data.isDistributeTippingActive;
    this._isTippingActive = data.isTippingActive;
    this._fee = this.isFeePercentage ? data.fee : data.fee / 100;
    this._dollar.load(data.dollar);
    this._percentage.load(data.percentage);
    this._fiveToTen = data.fiveToTen
      ? this._fiveToTen.load(data.fiveToTen)
      : this._dollar.load(data.dollar);
    this._distribution.load(data.distribution);
    return this;
  }

  public edit(data: MenuPaymentSettings): MenuPaymentSettingsBuilder {
    this._isFeeActive = data.isFeeActive;
    this._isFeePercentage = data.isFeePercentage ?? false;
    this._isDistributeTippingActive = data.isDistributeTippingActive;
    this._isTippingActive = data.isTippingActive;
    this._fee = data.fee;
    this._dollar.edit(data.dollar);
    this._fiveToTen.edit(data.fiveToTen);
    this._percentage.edit(data.percentage);
    this._distribution.edit(data.distribution);
    return this;
  }

  public toJSON = () => ({
    isFeeActive: this.isFeeActive,
    isTippingActive: this.isTippingActive,
    isFeePercentage: this.isFeePercentage ?? false,
    fee: this.isFeePercentage ? this.fee : this.fee * 100,
    isDistributeTippingActive: this.isDistributeTippingActive,
    dollar: this._dollar.toJSON(),
    fiveToTen: this._fiveToTen.toJSON(),
    percentage: this._percentage.toJSON(),
    distribution: this._distribution.toJSON()
  });

  build(): MenuPaymentSettings {
    return new MenuPaymentSettings(this);
  }
}
